<template>
  <BaseScreen v-bind="screen">
    <BaseAlert
      v-if="isErrorVisible"
      v-bind="error"
      slot="middle"
    />
    <Events
      v-bind="events"
      class="screen__strech screen__offset"
    >
      <template v-slot:content="{ items }">
        <Card
          v-for="item in items"
          :key="`event_item_${item.id}`"
          v-bind="item.performance"
          :date="item.date"
          :data-observe-id="item.id"
          :is-selected="item === selectedItem"
          v-observe="toggleObserver"
        >
          <template #actions>
            <BaseButton
              :text="$t('actions.addFeedback')"
              size="s"
              @click="pickEvent(item)"
            />
            <BaseButton
              :text="$t('actions.showSpectacleInfo')"
              size="s"
              @click="showInfo(item)"
            />
          </template>
        </Card>
      </template>
    </Events>
  </BaseScreen>
</template>

<script>
import { mapActions, mapMutations } from 'vuex';

import screenMixin from '@/mixins/screen';
import observerMixin from '@/mixins/observer';
import Events from '@/components/Events.vue';
import Card from '@/components/Card.vue';
import BaseButton from '@/components/BaseButton.vue';

import CONSTANTS from '@/helpers/constants';

const {
  STEP_FETCH_EVENTS_ERROR,
  ACTION_SCROLL_UP,
  ACTION_SCROLL_DOWN,
  ACTION_MORE,
  ACTION_PICK,
} = CONSTANTS;

export default {
  name: 'FeedbackEventsScreen',
  mixins: [screenMixin, observerMixin],
  components: {
    Events,
    Card,
    BaseButton,
  },
  data() {
    return {
      items: [],
      selectedItem: null,
    };
  },
  staticData: () => ({
    prevCard: null,
    nextCard: null,
  }),
  computed: {
    title() {
      return this.$t('titles.whichSpectacle');
    },
    pendingText() {
      return this.$t('pending.fetch');
    },
    events() {
      return {
        items: this.items,
        isSortAscending: false,
        actionNote: this.$t('texts.feedbackActionNote'),
      };
    },
    error() {
      return {
        title: this.$t('titles.badConnection'),
        text: this.$t('texts.feedbackNotPossible'),
        actions: [
          {
            text: this.$t('actions.home'),
            action: () => this.$router.push(this.$router.options.paths.home),
          },
        ],
      };
    },
  },
  created() {
    this.showPending(0);
    this.prefetch();
    this.initActions();
  },
  methods: {
    ...mapActions('cart', [
      'fetchPastEvents',
    ]),
    ...mapMutations('feedback', [
      'setEvent',
    ]),
    async prefetch() {
      try {
        this.items = await this.fetchPastEvents();
        this.hidePending();
      } catch {
        this.showError();
      }
    },
    pickEvent(item) {
      this.setEvent(item);
      this.$router.push(this.$router.options.paths.rateEvent);
    },
    pickSelectedItem() {
      if (this.selectedItem) {
        this.pickEvent(this.selectedItem);
      }
    },
    showInfo(item) {
      this.setEvent(item);
      this.$router.push(this.$router.options.paths.feedbackPerformance);
    },
    showSelectedInfo() {
      if (this.selectedItem) {
        this.showInfo(this.selectedItem);
      }
    },
    sayError() {
      this.$robot.dialogService.setCase(STEP_FETCH_EVENTS_ERROR, true, true);
      this.$robot.dialogService.sayReplicByName(STEP_FETCH_EVENTS_ERROR, true);
    },
    onObserverEnter(id, target) {
      const item = this.items.find((el) => el.id === id);
      this.selectedItem = item;
      this.prevCard = target.previousElementSibling;
      this.nextCard = target.nextElementSibling;
    },
    scrollUp() {
      return this.prevCard?.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    },
    scrollDown() {
      return this.nextCard?.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    },
    initActions() {
      const rootActions = [
        [ACTION_SCROLL_UP, this.scrollUp],
        [ACTION_SCROLL_DOWN, this.scrollDown],
        [ACTION_MORE, this.showSelectedInfo],
        [ACTION_PICK, this.pickSelectedItem],
      ];

      rootActions.forEach((action) => this.$root.$on(...action));

      this.$once('hook:beforeDestroy', () => {
        rootActions.forEach((action) => this.$root.$off(...action));
      });
    },
  },
};
</script>
